import { Users, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { IAiReplyContactOverride, TAiReplyPlatform } from '@/@types/aiReply';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: component needs convex Id type per CLAUDE.md exception

const PLATFORM_LABELS: Record<TAiReplyPlatform, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  gmail: 'Gmail',
};

interface IAiReplyContactOverridesProps {
  overrides: IAiReplyContactOverride[];
  onToggle: (
    contactIdentifier: string,
    platform: TAiReplyPlatform,
    field: 'disableAutoReply' | 'disableFollowUp' | 'disableStyleUsage',
    value: boolean,
    contactName?: string
  ) => Promise<void>;
  onDelete: (overrideId: Id<'aiReplyContactOverrides'>) => Promise<void>;
}

export function AiReplyContactOverrides({
  overrides,
  onToggle,
  onDelete,
}: IAiReplyContactOverridesProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users aria-hidden="true" className="h-4 w-4" />
          Contact Overrides
        </CardTitle>
      </CardHeader>
      <CardContent>
        {overrides.length === 0 ? (
          <div className="rounded-md border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              No overrides yet. Overrides are created from the contact panel in each messaging view.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead className="text-center">Disable Reply</TableHead>
                <TableHead className="text-center">Disable Follow-Up</TableHead>
                <TableHead className="text-center">Use My Style</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {overrides.map(override => (
                <TableRow key={override._id}>
                  <TableCell>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">
                        {override.contactName ?? override.contactIdentifier}
                      </p>
                      {override.contactName && (
                        <p className="text-xs text-muted-foreground">
                          {override.contactIdentifier}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {PLATFORM_LABELS[override.platform]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={override.disableAutoReply}
                      onCheckedChange={checked =>
                        void onToggle(
                          override.contactIdentifier,
                          override.platform,
                          'disableAutoReply',
                          checked,
                          override.contactName
                        )
                      }
                      aria-label={`Disable auto-reply for ${override.contactName ?? override.contactIdentifier}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={override.disableFollowUp}
                      onCheckedChange={checked =>
                        void onToggle(
                          override.contactIdentifier,
                          override.platform,
                          'disableFollowUp',
                          checked,
                          override.contactName
                        )
                      }
                      aria-label={`Disable follow-up for ${override.contactName ?? override.contactIdentifier}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={!(override.disableStyleUsage ?? false)}
                      onCheckedChange={checked =>
                        void onToggle(
                          override.contactIdentifier,
                          override.platform,
                          'disableStyleUsage',
                          !checked,
                          override.contactName
                        )
                      }
                      aria-label={`Use my style for ${override.contactName ?? override.contactIdentifier}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => void onDelete(override._id)}
                      aria-label={`Remove override for ${override.contactName ?? override.contactIdentifier}`}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
